import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Input, Button } from '@/components/ui';
import { useSystemSettings } from '../hooks/useSystemSettings';

const localizationSchema = z.object({
  defaultLanguage: z.string(),
  timezone: z.string(),
  dateFormat: z.string(),
  currency: z.string(),
  numberFormat: z.enum(['comma-decimal', 'dot-comma']),
});

type LocalizationFormValues = z.infer<typeof localizationSchema>;

interface LocalizationSectionProps {
  onChange?: (isDirty: boolean) => void;
  onSaveSuccess?: () => void;
}

export function LocalizationSection({ onChange, onSaveSuccess }: LocalizationSectionProps) {
  const { localization, updateLocalization } = useSystemSettings();

  const { register, handleSubmit, reset, formState: { errors, isDirty } } = useForm<LocalizationFormValues>({
    resolver: zodResolver(localizationSchema),
    defaultValues: localization || {}
  });

  // Sync data when loaded
  React.useEffect(() => {
    if (localization) {
      reset(localization);
    }
  }, [localization, reset]);

  React.useEffect(() => {
    if (onChange) {
      onChange(isDirty);
    }
  }, [isDirty, onChange]);

  const onSubmit = async (data: LocalizationFormValues) => {
    try {
      await updateLocalization(data);
      if (onSaveSuccess) onSaveSuccess();
    } catch (err) {
      // Error handled by mutation
    }
  };

  return (
    <form id="localization-form" onSubmit={handleSubmit(onSubmit)} className="space-y-8">
      {/* Basic Localization */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="space-y-2">
          <label className="text-sm font-bold text-slate-700">Ngôn ngữ mặc định</label>
          <select
            {...register('defaultLanguage')}
            className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-600/20 transition-all"
          >
            <option value="vi">Tiếng Việt (Vietnamese)</option>
            <option value="en">Tiếng Anh (English)</option>
          </select>
        </div>

        <div className="space-y-2">
          <label className="text-sm font-bold text-slate-700">Múi giờ hệ thống</label>
          <select
            {...register('timezone')}
            className="w-full rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-600/20 transition-all"
          >
            <option value="Asia/Ho_Chi_Minh">(GMT+07:00) Bangkok, Hanoi, Jakarta</option>
            <option value="UTC">(GMT+00:00) UTC</option>
            <option value="Europe/London">(GMT+00:00) London</option>
          </select>
        </div>
      </div>

      {/* Formats */}
      <div className="bg-slate-50/50 p-6 rounded-2xl border border-slate-100 space-y-6">
        <h4 className="text-xs font-black text-slate-400  tracking-widest mb-2">Định dạng hiển thị</h4>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <label className="text-sm font-bold text-slate-700">Định dạng ngày tháng</label>
            <select
              {...register('dateFormat')}
              className="w-full bg-white rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-600/20 transition-all"
            >
              <option value="DD/MM/YYYY">31/12/2026</option>
              <option value="YYYY-MM-DD">2026-12-31</option>
              <option value="MM/DD/YYYY">12/31/2026</option>
            </select>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-bold text-slate-700">Định dạng số (Thập phân)</label>
            <select
              {...register('numberFormat')}
              className="w-full bg-white rounded-xl border border-slate-200 px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-600/20 transition-all"
            >
              <option value="comma-decimal">1.234,56 (Dấu phẩy thập phân)</option>
              <option value="dot-comma">1,234.56 (Dấu chấm thập phân)</option>
            </select>
          </div>
        </div>
      </div>

      {/* Finance */}
      <div className="space-y-2">
        <label className="text-sm font-bold text-slate-700">Đơn vị tiền tệ chính</label>
        <Input
          {...register('currency')}
          placeholder="Ví dụ: VND, USD, EUR"
        />
        <p className="text-[10px] text-slate-400 font-medium">Đơn vị tiền tệ này sẽ được áp dụng cho toàn bộ các báo cáo tài chính và hóa đơn trong hệ thống.</p>
      </div>
    </form>
  );
}
